import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Quote } from 'lucide-react';
import { getTestimonials } from '@/data/site';
import { useLiveTick } from '@/lib/liveContent';
import { SectionHeader } from './SectionHeader';
import { cn } from '@/lib/utils';

export const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const reduce = useReducedMotion();
  useLiveTick();
  const TESTIMONIALS = getTestimonials();
  const current = TESTIMONIALS[index];

  const prev = () => setIndex((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const next = () => setIndex((i) => (i + 1) % TESTIMONIALS.length);

  return (
    <section aria-labelledby="testimonials-heading" data-testid="testimonials-section" className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <SectionHeader
          index="06"
          eyebrow="Client Feedback"
          title="What our clients say"
          testid="testimonials-header"
        />

        <div
          role="region"
          aria-roledescription="carousel"
          aria-label="Client testimonials"
          data-testid="testimonial-carousel"
          className="relative mt-14"
        >
          <Quote className="mx-auto h-10 w-10 text-gold-700" aria-hidden="true" />

          <div aria-live="polite" className="mt-8 min-h-[220px] sm:min-h-[190px]">
            <AnimatePresence mode="wait">
              <motion.figure
                key={index}
                initial={reduce ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0, y: -12 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="text-center"
                data-testid="testimonial-card"
              >
                <blockquote className="mx-auto max-w-3xl font-serif text-xl font-medium italic leading-relaxed text-navy-800 sm:text-2xl">
                  “{current.quote}”
                </blockquote>
                <figcaption className="mt-8">
                  <span className="block text-base font-bold text-navy-900">{current.name}</span>
                  <span className="mt-1 block text-sm font-medium text-slate-500">{current.company}</span>
                </figcaption>
              </motion.figure>
            </AnimatePresence>
          </div>

          <div className="mt-10 flex items-center justify-center gap-6">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous testimonial"
              data-testid="testimonial-prev-btn"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-navy-900 transition-colors hover:border-navy-900 hover:bg-navy-900 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <div className="flex items-center gap-2" role="tablist" aria-label="Testimonial pages">
              {TESTIMONIALS.map((t, i) => (
                <button
                  key={t.name}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`Testimonial ${i + 1} of ${TESTIMONIALS.length}: ${t.name}, ${t.company}`}
                  data-testid={`testimonial-dot-${i}`}
                  onClick={() => setIndex(i)}
                  className={cn(
                    'h-2 rounded-full transition-[width,background-color] duration-300',
                    i === index ? 'w-7 bg-gold-600' : 'w-2 bg-slate-300 hover:bg-slate-400'
                  )}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={next}
              aria-label="Next testimonial"
              data-testid="testimonial-next-btn"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-navy-900 transition-colors hover:border-navy-900 hover:bg-navy-900 hover:text-white"
            >
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
